import { useCallback, useState } from 'react';

import type { TranslationGenerateModalProps } from './TranslationGenerateModal';

/**
 * Owns the open/closed state and target preselect for `TranslationGenerateModal`,
 * so each surface (LeftSidePanel, ClipTranscriptBridge, TranscriptSpeakersContent,
 * CaptionsContent) doesn't have to re-implement the same wiring.
 *
 * Returned values:
 * - `isOpen`         — whether the modal is currently mounted.
 * - `openCreate()`   — open in create mode (no preselect).
 * - `openReplace(l)` — open in replace mode preselected to `l` (used by Regenerate).
 * - `close()`        — close + clear preselect.
 * - `modalProps`     — `{ initialTargetLanguage, onClose }` to spread onto the modal.
 *                      The caller still owns `onSuccess` and `supportedLanguages`
 *                      because both depend on local state (selected language sync,
 *                      hydration source).
 *
 * The Toggle's `onRequestGenerate(options)` dispatches to either path:
 *   options?.preselectedTargetLang ? openReplace(...) : openCreate()
 */
export function useTranslationGenerateModalController(): {
  isOpen: boolean;
  openCreate: () => void;
  openReplace: (preselectedTargetLang: string) => void;
  close: () => void;
  onRequestGenerate: (options?: { preselectedTargetLang?: string }) => void;
  modalProps: Pick<
    TranslationGenerateModalProps,
    'initialTargetLanguage' | 'onClose'
  >;
} {
  const [isOpen, setIsOpen] = useState(false);
  const [preselect, setPreselect] = useState<string | undefined>(undefined);

  const close = useCallback(() => {
    setIsOpen(false);
    setPreselect(undefined);
  }, []);

  const openCreate = useCallback(() => {
    setPreselect(undefined);
    setIsOpen(true);
  }, []);

  const openReplace = useCallback((preselectedTargetLang: string) => {
    setPreselect(preselectedTargetLang);
    setIsOpen(true);
  }, []);

  const onRequestGenerate = useCallback(
    (options?: { preselectedTargetLang?: string }) => {
      if (options?.preselectedTargetLang) {
        openReplace(options.preselectedTargetLang);
      } else {
        openCreate();
      }
    },
    [openCreate, openReplace],
  );

  return {
    isOpen,
    openCreate,
    openReplace,
    close,
    onRequestGenerate,
    modalProps: {
      initialTargetLanguage: preselect,
      onClose: close,
    },
  };
}
